import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { useEffect } from "react";
import WelcomeSplash from "./components/WelcomeSplash";
import ChatWidget from "./components/ChatWidget";

import { NotificationProvider } from "./contexts/NotificationContext";

// Pages
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Players from "./pages/Players";
import PlayerDetail from "./pages/PlayerDetail";
import Matches from "./pages/Matches";
import MatchEdit from "./pages/MatchEdit";
import Training from "./pages/Training";
import Seasons from "./pages/Seasons";
import EditSeason from "./pages/EditSeason";
import Leaderboard from "./pages/Leaderboard";
import LeagueTable from "./pages/LeagueTable";
import Settings from "./pages/Settings";
import TeamKitty from "./pages/TeamKitty";
import Fines from "./pages/Fines";
import TeamMembers from "./pages/TeamMembers";
import JoinTeam from "./pages/JoinTeam";
import VerifyEmail from "./pages/VerifyEmail";
import PlayerProfile from "./pages/PlayerProfile";
import Onboarding from "./pages/Onboarding";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import LandingPage from "./pages/LandingPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";
import MaintenancePage from "./pages/MaintenancePage";
import RateLimitedPage from "./pages/RateLimitedPage";
import PricingSliderMockup from "./pages/PricingSliderMockup";
import CrashSandbox from "./pages/CrashSandbox";
import AuditLogPage from "./pages/AuditLogPage";
import ErrorBoundary from "./components/ErrorBoundary";
import AppContentManager from "./pages/AppContentManager";
import SeasonOverview from "./pages/SeasonOverview";
import CoachesOffice from "./pages/CoachesOffice";
import PublicCoachProfile from "./pages/PublicCoachProfile";
import TacticsBoard from "./pages/TacticsBoard";
import ToDo from "./pages/ToDo";
import Journal from "./pages/Journal";
import Welfare from "./pages/Welfare";
import AiTrainingPlans from "./pages/AiTrainingPlans";
import MatchNotes from "./pages/MatchNotes";
import DressingRoom from "./pages/DressingRoom";
import Forum from "./pages/Forum";
import ForumCategory from "./pages/ForumCategory";
import ForumThread from "./pages/ForumThread";
import About from "./pages/About";
import ChatDashboard from "./pages/ChatDashboard";
import MatchDayMode from "./pages/MatchDayMode";
import PaymentSuccess from "./pages/PaymentSuccess";
import AuthCallback from "./pages/AuthCallback";
import ClapPreview from "./pages/ClapPreview";
import OpsPage from "./pages/OpsPage";
import TeamChatList from "./pages/TeamChatList";
import TeamChatManage from "./pages/TeamChatManage";
import TeamChatJoin from "./pages/TeamChatJoin";
import TeamChatView from "./pages/TeamChatView";

// Component to apply user's theme settings as CSS variables
const ThemeProvider = ({ children }) => {
  const { user, showSplash, hideSplash } = useAuth();
  
  useEffect(() => {
    const buttonColor = user?.button_color || '#064E3B';
    document.documentElement.style.setProperty('--primary-button-color', buttonColor);
    
    // Calculate hover color (slightly lighter/darker)
    const hoverColor = adjustColor(buttonColor, 15);
    document.documentElement.style.setProperty('--primary-button-hover', hoverColor);
  }, [user?.button_color]);
  
  return (
    <>
      {showSplash && user && <WelcomeSplash onComplete={hideSplash} />}
      {children}
    </>
  );
};

// Helper function to adjust color brightness
const adjustColor = (color, percent) => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

// TeamChat landing router — coaches see the manager list, chat_members
// see their own restricted feed. This lives inside <ProtectedRoute> so
// user is guaranteed to be set.
const TeamChatListOrView = () => {
  const { user } = useAuth();
  if (user?.role === 'chat_member') {
    return <TeamChatView />;
  }
  return <TeamChatList />;
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, showSplash, splashColor, isAdmin, isSuperAdmin, needsOnboarding, needsSubscription } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Chat members are locked down to team-chat routes + settings only.
  // Any other path (dashboard, players, matches, etc.) redirects them
  // straight to their default chat. Restricted-role enforcement is
  // frontend-level for M1; individual feature endpoints will re-check
  // permissions in later milestones.
  if (user?.role === 'chat_member') {
    const allowed = window.location.pathname.startsWith('/team-chat')
      || window.location.pathname === '/settings'
      || window.location.pathname === '/my-profile';
    if (!allowed) {
      return <Navigate to="/team-chat" replace />;
    }
    // chat_member reaching /team-chat with no chatId → resolve on landing page
    return children;
  }

  // Super admin: redirect from coach-only landing pages to their dashboard
  const superAdminAllowedPaths = ['/super-admin', '/tactics-board', '/coaches-office', '/dressing-room', '/forum', '/todo', '/journal', '/match-notes', '/welfare', '/ai-plans'];
  if (isSuperAdmin && !superAdminAllowedPaths.some(p => window.location.pathname.startsWith(p))) {
    return <Navigate to="/super-admin" replace />;
  }

  // Coaches need onboarding first
  if (needsOnboarding && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Coaches who completed onboarding but have no subscription → plan selection
  if (needsSubscription && window.location.pathname !== '/onboarding' && window.location.pathname !== '/payment-success') {
    return <Navigate to="/onboarding?step=plan" replace />;
  }

  // Admin-only routes
  if (adminOnly && !isAdmin) {
    return <Navigate to="/clubhouse" replace />;
  }
  
  // While splash is showing, render a matching background to prevent white flash
  if (showSplash && splashColor) {
    return (
      <div 
        className="min-h-screen" 
        style={{ backgroundColor: splashColor }}
      />
    );
  }
  
  return children;
};

// Super Admin Route
const SuperAdminRoute = ({ children }) => {
  const { user, loading, isSuperAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/clubhouse" replace />;
  }
  
  return children;
};

// Player-only route
const PlayerRoute = ({ children }) => {
  const { user, loading, isPlayer } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isPlayer) {
    return <Navigate to="/clubhouse" replace />;
  }
  
  return children;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#064E3B]">
        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/clubhouse" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page - Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/__crash" element={<CrashSandbox />} />
      <Route path="/about" element={<About />} />
      
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password/:token" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      
      {/* Player Join/Verify Routes (public) */}
      <Route path="/join/:token" element={<JoinTeam />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      
      {/* Onboarding Route (public - auth happens at payment step) */}
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/clap-preview" element={<ClapPreview />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Super Admin Routes */}
      <Route path="/super-admin" element={<SuperAdminRoute><NotificationProvider><SuperAdminDashboard /></NotificationProvider></SuperAdminRoute>} />
      <Route path="/super-admin/content" element={<SuperAdminRoute><NotificationProvider><AppContentManager /></NotificationProvider></SuperAdminRoute>} />
      <Route path="/super-admin/chat" element={<SuperAdminRoute><NotificationProvider><ChatDashboard /></NotificationProvider></SuperAdminRoute>} />
      <Route path="/super-admin/ops" element={<SuperAdminRoute><OpsPage /></SuperAdminRoute>} />
      <Route path="/super-admin/audit-log" element={<SuperAdminRoute><AuditLogPage /></SuperAdminRoute>} />
      
      {/* Protected Routes */}
      <Route path="/clubhouse" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/players" element={<ProtectedRoute><Players /></ProtectedRoute>} />
      <Route path="/players/:playerId" element={<ProtectedRoute><PlayerDetail /></ProtectedRoute>} />
      <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
      <Route path="/matches/new" element={<ProtectedRoute adminOnly><MatchEdit /></ProtectedRoute>} />
      <Route path="/matches/:matchId/edit" element={<ProtectedRoute><MatchEdit /></ProtectedRoute>} />
      <Route path="/matches/:matchId/matchday" element={<ProtectedRoute><MatchDayMode /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/league-table" element={<ProtectedRoute><LeagueTable /></ProtectedRoute>} />
      <Route path="/team-kitty" element={<ProtectedRoute><TeamKitty /></ProtectedRoute>} />
      <Route path="/fines" element={<ProtectedRoute><Fines /></ProtectedRoute>} />
      
      {/* Admin Only Routes */}
      <Route path="/seasons" element={<ProtectedRoute adminOnly><Seasons /></ProtectedRoute>} />
      <Route path="/seasons/:seasonId/edit" element={<ProtectedRoute adminOnly><EditSeason /></ProtectedRoute>} />
      <Route path="/season-overview" element={<ProtectedRoute adminOnly><SeasonOverview /></ProtectedRoute>} />
      <Route path="/coaches-office" element={<ProtectedRoute adminOnly><CoachesOffice /></ProtectedRoute>} />
      <Route path="/tactics-board" element={<ProtectedRoute adminOnly><TacticsBoard /></ProtectedRoute>} />
      <Route path="/todo" element={<ProtectedRoute adminOnly><ToDo /></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute adminOnly><Journal /></ProtectedRoute>} />
      <Route path="/welfare" element={<ProtectedRoute adminOnly><Welfare /></ProtectedRoute>} />
      <Route path="/ai-plans" element={<ProtectedRoute adminOnly><AiTrainingPlans /></ProtectedRoute>} />
      <Route path="/match-notes" element={<ProtectedRoute adminOnly><MatchNotes /></ProtectedRoute>} />
      <Route path="/dressing-room" element={<ProtectedRoute><DressingRoom /></ProtectedRoute>} />
      <Route path="/forum" element={<ProtectedRoute><Forum /></ProtectedRoute>} />
      <Route path="/forum/category/:categoryId" element={<ProtectedRoute><ForumCategory /></ProtectedRoute>} />
      <Route path="/forum/thread/:threadId" element={<ProtectedRoute><ForumThread /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
      <Route path="/team-members" element={<ProtectedRoute adminOnly><TeamMembers /></ProtectedRoute>} />
      
      {/* Team Chat routes — coach + chat_member. Public join route MUST
          be listed first so it matches before the :chatId dynamic route. */}
      <Route path="/team-chat/join/:token" element={<TeamChatJoin />} />
      <Route path="/team-chat" element={<ProtectedRoute><TeamChatListOrView /></ProtectedRoute>} />
      <Route path="/team-chat/:chatId" element={<ProtectedRoute><TeamChatView /></ProtectedRoute>} />
      <Route path="/team-chat/:chatId/manage" element={<ProtectedRoute adminOnly><TeamChatManage /></ProtectedRoute>} />

      {/* Player Only Routes */}
      <Route path="/my-profile" element={<PlayerRoute><PlayerProfile /></PlayerRoute>} />
      
      {/* Fallback Redirect */}
      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route path="/var-review" element={<RateLimitedPage />} />
      <Route path="/pricing-slider-mockup" element={<PricingSliderMockup />} />
      {/* Public Coach Profile — share link. Must be listed BEFORE the
          catch-all NotFound. No auth guard. */}
      <Route path="/coach/:slug" element={<PublicCoachProfile />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function AppWithChat() {
  const { user } = useAuth();
  const location = useLocation();
  const isMatchDay = location.pathname.includes('/matchday');
  const isOnboarding = location.pathname.startsWith('/onboarding');
  
  return (
    <>
      <ThemeProvider>
        <AppRoutes />
        {user?.role !== 'super_admin' && !isMatchDay && !isOnboarding && <ChatWidget user={user} />}
        <Toaster />
      </ThemeProvider>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppWithChat />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
